import { useEffect, useState } from "react";

import { Alert, Button, Tab, TabList, Textarea } from "@realroof/ui";

import { api, canModerate, type ReviewItem } from "../api";
import { when } from "../format";
import { Link } from "../router";
import { useSession } from "../session";
import { Table, td, th } from "../table";

type Filter = "pending" | "published" | "rejected";
const TABS: [Filter, string][] = [
  ["pending", "To check"],
  ["published", "Published"],
  ["rejected", "Not published"],
];
const TAG_LABEL: Record<string, string> = { fake_misleading: "Fake / misleading" };
const tagLabel = (t: string) => TAG_LABEL[t] ?? t.replace(/_/g, " ");

/** Written reviews wait here before they show on the ad (stars count at once). Oldest first. */
export function Reviews() {
  const { me } = useSession();
  const [filter, setFilter] = useState<Filter>("pending");
  const [rows, setRows] = useState<ReviewItem[] | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [removeRating, setRemoveRating] = useState(false);
  const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);

  const load = (f: Filter) =>
    api<ReviewItem[]>(`/admin/reviews?status=${f}`)
      .then(setRows)
      .catch((err: Error) => setMessage({ kind: "error", text: err.message }));
  useEffect(() => {
    setRows(null);
    load(filter);
  }, [filter]);

  async function act(id: string, action: "approve" | "reject") {
    setMessage(null);
    try {
      await api(`/admin/reviews/${id}/${action}`, {
        method: "POST",
        body: action === "reject" ? { reason: reason.trim(), remove_rating: removeRating } : undefined,
      });
      setMessage({
        kind: "success",
        text: action === "approve" ? "Published on the ad." : removeRating ? "Not published; the rating was removed too." : "Not published; the rating still counts.",
      });
      setRejecting(null);
      setReason("");
      setRemoveRating(false);
      await load(filter);
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message });
    }
  }

  const moderator = canModerate(me?.staff_role);
  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Reviews</h1>
      <TabList>
        {TABS.map(([f, label]) => (
          <Tab key={f} selected={f === filter} className="flex-none" onClick={() => setFilter(f)}>
            {label}
          </Tab>
        ))}
      </TabList>
      {message && <Alert tone={message.kind}>{message.text}</Alert>}
      {rows === null && <p className="text-muted-foreground">Loading…</p>}
      {rows?.length === 0 && <p className="text-muted-foreground">{filter === "pending" ? "No reviews to check." : "Nothing here."}</p>}
      {rows && rows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Ad</th>
              <th className={th}>Review</th>
              <th className={th}>By</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className={td}>
                  <Link to={`/ads/${r.listing_id}`} className="font-semibold">
                    {r.title}
                  </Link>
                  <div className="font-mono text-xs text-muted-foreground">{r.public_id}</div>
                </td>
                <td className={`${td} max-w-xl`}>
                  <span aria-label={`${r.stars} out of 5`}>{"★".repeat(r.stars) + "☆".repeat(5 - r.stars)}</span>
                  {r.visited && <span className="text-sm text-muted-foreground"> · visited</span>}
                  {r.tags.length > 0 && <div className="text-sm text-muted-foreground">{r.tags.map(tagLabel).join(", ")}</div>}
                  <p className="m-0 mt-1">{r.text}</p>
                  {r.reject_reason && (
                    <div className="text-sm text-muted-foreground">
                      Why: {r.reject_reason}
                      {r.removed && " · rating removed"}
                    </div>
                  )}
                </td>
                <td className={`${td} whitespace-nowrap`}>
                  {r.author}
                  <div className="text-xs text-muted-foreground">{r.author_mobile}</div>
                  <div className="text-xs text-muted-foreground">{when(r.updated_at)}</div>
                </td>
                <td className={td}>
                  {filter === "pending" && moderator && rejecting !== r.id && (
                    <div className="flex flex-col gap-2">
                      <Button size="sm" variant="primary" onClick={() => act(r.id, "approve")}>
                        Publish
                      </Button>
                      <Button size="sm" onClick={() => setRejecting(r.id)}>
                        Don’t publish…
                      </Button>
                    </div>
                  )}
                  {rejecting === r.id && (
                    <div className="flex min-w-60 flex-col gap-2">
                      <Textarea rows={2} aria-label="Reason the author will see" placeholder="The author sees this" value={reason} onChange={(e) => setReason(e.target.value)} />
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={removeRating} onChange={(e) => setRemoveRating(e.target.checked)} /> Remove the rating too (fake or abusive)
                      </label>
                      <div className="flex gap-2">
                        <Button size="sm" variant="destructive" disabled={reason.trim().length < 5} onClick={() => act(r.id, "reject")}>
                          Don’t publish
                        </Button>
                        <Button size="sm" variant="link" onClick={() => setRejecting(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}
